/**
 * Simple Bundles – variant-scoped Infinite Options image swap (19 Oct 2026)
 *
 * Swaps the main PDP image when a customer changes an Infinite Options
 * bundle dropdown. Uses the bundled_variants metafield for reliable
 * product/variant → image mapping. Should work acrosss any Shopify theme.
 *
 * ─────────────────────────────────────────────────────────────────────
 * INSTALLATION (pick one):
 *
 *   Option A – Theme asset (recommended):
 *     1. Upload this file to your theme's assets/ folder
 *     2. Add before </body> in layout/theme.liquid:
 *        <script src="{{ 'sb-image-swap.js' | asset_url }}" defer></script>
 *
 *   Option B – Inline snippet:
 *     Wrap in <script>…</script> at the bottom of your product template
 *
 * ─────────────────────────────────────────────────────────────────────
 * HOW IT WORKS:
 *
 *   1. On page load, fetches the current product's metafields via
 *      /products/{handle}.json to get the bundled_variants data
 *   2. Builds a lookup map: option display text → { handle, variant_id }
 *   3. Fetches each child product once (cached) to get all variant images
 *   4. On dropdown change, looks up the selection → swaps the main image
 *
 *   Because it uses the actual metafield data (handles + variant IDs),
 *   it never has to guess product handles from display text.
 * ─────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  // ========================================================================
  // CONFIG
  // ========================================================================
  const CONFIG = {
    // Selectors to find the main product image – tried in order. Most themes use these selectors .
    // Add your theme's selector at the top if none of these match.
    imageSelectors: [
      'product-gallery .product-gallery__media.is-initial img',
      'product-gallery .product-gallery__media img',
      '.product__media-item--variant img',
      '.product__media img.image-magnify-full-size',
      '.product__media-list .product__media img',
      '.product__media img',
      '.product-image-main img',
      '.product__photos img',
      '.product__photo img.photoswipe__image',
      '.product__photo img',
      '.product-gallery__image img',
      '.product__main-photos img',
      '.product-main-slide .image-wrap img',
      '.product-featured-img',
      '.product-single__photo img',
      '.product-single__media img',
      '.product__media-item img',
      '[data-product-media-type="image"] img',
      '.product-single__image img',
      '.product__image-wrapper img',
    ],

    ioBundleContainer: '#simple-bundles-io-options',
    transitionMs: 200,
    resetLabel: 'Vis modelbillede',
    mobileMaxWidth: 749,
    mobileScrollOffset: 90,
  };

  // ========================================================================
  // STATE
  // ========================================================================
  let mainImageEl = null;
  let originalSrc = null;
  let originalSrcset = null;
  let originalSizes = null;
  let originalPictureSources = [];
  let originalZoomAttributes = null;
  let resetControlEl = null;

  // Map: normalized option text → image URL
  // e.g. "cotton canvas cap" → "https://cdn.shopify.com/..."
  const imageMap = new Map();
  let activeParentVariantId = null;
  let imageMapReady = Promise.resolve();
  let selectionRevision = 0;
  let imageTimer = null;

  // Cache for /products/{handle}.js fetches
  const productCache = {};

  // ========================================================================
  // HELPERS
  // ========================================================================

  /**
   * Normalize option text for reliable matching.
   * Strips price suffixes like "(+CA$5.00)", whitespace, and lowercases.
   */
  function normalizeOptionText(text) {
    return String(text || '')
      .replace(/\([\+\-]?\s*[A-Z]{0,3}\$?\s*[\d,.]+\)/g, '') // (+CA$5.00), (-$3.00), etc.
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  /**
   * Find the main product image on the page.
   * Tries configured selectors, then falls back to largest image in product area.
   */
  function findMainImage() {
    for (const sel of CONFIG.imageSelectors) {
      const img = document.querySelector(sel);
      if (img && img.src) return img;
    }

    // Fallback: largest image in the product section
    const section =
      document.querySelector('.product') ||
      document.querySelector('[data-section-type="product"]') ||
      document.querySelector('.product-single') ||
      document.querySelector('main');

    if (!section) return null;

    let best = null;
    let bestArea = 0;
    section.querySelectorAll('img').forEach((img) => {
      const area = img.offsetWidth * img.offsetHeight;
      if (area > bestArea && !img.closest(CONFIG.ioBundleContainer)) {
        best = img;
        bestArea = area;
      }
    });
    return best;
  }

  /**
   * Get the current product handle from the URL.
   */
  function getProductHandle() {
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  /**
   * Fetch product JSON (cached).
   */
  async function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return null;
      const data = await res.json();
      productCache[handle] = data;
      return data;
    } catch (e) {
      console.warn('[SB-IMG] fetch failed for', handle, e);
      return null;
    }
  }

  /**
   * Resize a Shopify CDN image URL to a given width.
   */
  function resizeImage(url, width) {
    if (!url) return url;
    // Remove any existing size suffix and add desired one
    return url
      .replace(/_(\d+x\d*|\d*x\d+)\./, '.')
      .replace(/\.(\w+)(\?.*)?$/, `_${width}x.$1$2`);
  }

  /**
   * Save every responsive-image value used by Prestige before the first swap.
   * Mobile may render a <source> from <picture> instead of the img src itself.
   */
  function captureOriginalImageState() {
    if (!mainImageEl) return;

    originalSrc = mainImageEl.getAttribute('src') || mainImageEl.src;
    originalSrcset = mainImageEl.getAttribute('srcset');
    originalSizes = mainImageEl.getAttribute('sizes');

    const picture = mainImageEl.closest('picture');
    originalPictureSources = picture
      ? Array.from(picture.querySelectorAll('source')).map((source) => ({
          element: source,
          srcset: source.getAttribute('srcset'),
          sizes: source.getAttribute('sizes'),
        }))
      : [];

    const zoomEl =
      mainImageEl.closest('[data-zoom]') ||
      mainImageEl.closest('[data-bgset]') ||
      mainImageEl.closest('[data-image]');

    originalZoomAttributes = zoomEl
      ? {
          element: zoomEl,
          zoom: zoomEl.getAttribute('data-zoom'),
          bgset: zoomEl.getAttribute('data-bgset'),
          image: zoomEl.getAttribute('data-image'),
        }
      : null;
  }

  let yarnThumb = null;
  let modelThumb = null;
  let lastYarnImage = null;
  let selectedYarnImages = [];
  let yarnChoiceCount = 0;

  function updateYarnThumbnail() {
    if (!yarnThumb) return;
    const multiple = yarnChoiceCount > 1;
    const caption = yarnThumb.lastElementChild;
    const label = multiple ? 'Valgte farver' : 'Valgt garn';
    caption.textContent = label;
    yarnThumb.setAttribute('aria-label', multiple && selectedYarnImages.length
      ? `${label}: ${selectedYarnImages.map(item => item.label).join(', ')}`
      : label);
    yarnThumb.title = multiple ? selectedYarnImages.map(item => item.label).join('\n') : '';
    yarnThumb.hidden = !lastYarnImage;
    let preview = yarnThumb.querySelector('[data-sb-yarn-preview]');
    if (!multiple && !preview) {
      const img = yarnThumb.querySelector('img');
      if (img && lastYarnImage) img.src = lastYarnImage;
      return;
    }
    if (!preview) {
      preview = document.createElement('span');
      preview.setAttribute('data-sb-yarn-preview', '');
      preview.style.cssText = 'display:flex;width:72px;height:80px;overflow:hidden;gap:1px';
      yarnThumb.querySelector('img').replaceWith(preview);
    }
    const images = multiple ? selectedYarnImages : lastYarnImage ? [{src:lastYarnImage,label}] : [];
    preview.replaceChildren(...images.map(item => {
      const img = document.createElement('img');
      img.src = resizeImage(item.src, 160);
      img.alt = item.label;
      img.width = 72;
      img.height = 80;
      img.style.cssText = `display:block;flex:1 1 0;min-width:0;width:0;height:80px;object-fit:${multiple ? 'cover' : 'contain'}`;
      return img;
    }));
  }

  function collectYarnSelections() {
    const groups = imageMap.get(selectedParentId()) || imageMap.get('');
    const seen = new Set();
    selectedYarnImages = [];
    yarnChoiceCount = 0;
    document.querySelectorAll(`${CONFIG.ioBundleContainer} select`).forEach(select => {
      const property = (select.name || '').match(/^properties\[(.*)\]$/)?.[1];
      const label = select.labels?.[0]?.textContent?.trim();
      const name = property || label || select.name || '';
      const key = normalizeOptionText(name);
      if (select.disabled || !groups?.has(key) || seen.has(key)) return;
      seen.add(key);
      yarnChoiceCount++;
      const src = resolveImage(select);
      if (src) selectedYarnImages.push({src, label:`${name}: ${select.options[select.selectedIndex].textContent.trim()}`});
    });
    if (!selectedYarnImages.some(item => item.src === lastYarnImage)) {
      lastYarnImage = selectedYarnImages[selectedYarnImages.length - 1]?.src || null;
    }
    updateYarnThumbnail();
  }
  function setResetControlVisible(visible) {
    if (!resetControlEl) return;
    if (visible) resetControlEl.hidden = false;
    modelThumb?.setAttribute('aria-pressed', String(!visible));
    yarnThumb?.setAttribute('aria-pressed', String(visible));
    for (const button of [modelThumb, yarnThumb]) {
      if (button) button.style.borderColor = button.getAttribute('aria-pressed') === 'true' ? '#222' : '#ddd';
    }
  }

  /**
   * On mobile the option controls sit below the gallery. After the new yarn
   * image is ready, move the customer back to the image they just selected.
   */
  function scrollToChangedImage() {
    if (!mainImageEl || window.innerWidth > CONFIG.mobileMaxWidth) return;

    const gallery =
      mainImageEl.closest('product-gallery') ||
      mainImageEl.closest('.product-gallery') ||
      mainImageEl.closest('.product__media-wrapper') ||
      mainImageEl;

    const top = gallery.getBoundingClientRect().top + window.scrollY - CONFIG.mobileScrollOffset;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  /**
   * Add one link-style reset button directly below the complete product gallery.
   * It is only visible after an Infinite Options image swap.
   */
  function ensureResetControl() {
    if (resetControlEl && document.body.contains(resetControlEl)) return;

    const gallery =
      mainImageEl?.closest('product-gallery') ||
      mainImageEl?.closest('.product-gallery') ||
      mainImageEl?.closest('.product__media-wrapper');

    if (!gallery) return;

    resetControlEl = document.createElement('div');
    resetControlEl.setAttribute('role', 'group');
    resetControlEl.setAttribute('aria-label', 'Vælg produktbillede');
    resetControlEl.hidden = true;
    resetControlEl.style.cssText = 'margin-top:12px;gap:10px;align-items:start';
    const makeThumb = (label, src, click) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.setAttribute('aria-label', label);
      button.style.cssText = 'display:inline-flex;flex-direction:column;align-items:center;gap:4px;width:84px;padding:4px;border:1px solid #ddd;background:transparent;color:inherit;cursor:pointer;margin-right:8px;font:inherit;font-size:11px';
      const img = document.createElement('img');
      img.src = src;
      img.alt = label;
      img.width = 72;
      img.height = 80;
      img.style.cssText = 'width:72px;height:80px;object-fit:contain';
      const caption = document.createElement('span');
      caption.textContent = label;
      button.append(img, caption);
      button.addEventListener('click', click);
      resetControlEl.append(button);
      return button;
    };
    modelThumb = makeThumb('Modelbillede', originalSrc, restoreOriginal);
    yarnThumb = makeThumb('Valgt garn', lastYarnImage || originalSrc, () => {
      if (lastYarnImage) swapImage(lastYarnImage);
    });
    yarnThumb.hidden = !lastYarnImage;
    updateYarnThumbnail();
    const imageList = gallery.querySelector('.product-gallery__image-list');
    if (imageList) imageList.insertAdjacentElement('afterend', resetControlEl);
    else gallery.append(resetControlEl);
  }

  /**
   * Swap the main image with a fade transition.
   */
  function swapImage(newSrc) {
    if (!mainImageEl || !newSrc) return;

    lastYarnImage = newSrc;
    ensureResetControl();
    updateYarnThumbnail();
    // Avoid swapping to the same image
    const normCurrent = mainImageEl.src.replace(/[?#].*$/, '').replace(/_\d+x\d*\./, '.');
    const normNew = newSrc.replace(/[?#].*$/, '').replace(/_\d+x\d*\./, '.');
    if (normCurrent === normNew) {
      ensureResetControl();
      setResetControlVisible(true);
      scrollToChangedImage();
      return;
    }

    mainImageEl.style.transition = `opacity ${CONFIG.transitionMs}ms ease`;
    mainImageEl.style.opacity = '0';

    clearTimeout(imageTimer);
    imageTimer = setTimeout(() => {
      // Update both img and every responsive <source>. Mobile browsers often
      // render a <source> instead of img.src, so changing only src is not enough.
      mainImageEl.setAttribute('srcset', newSrc);
      const picture = mainImageEl.closest('picture');
      if (picture) {
        picture.querySelectorAll('source').forEach((source) => {
          source.setAttribute('srcset', newSrc);
        });
      }

      // Update zoom / lightbox wrappers
      const zoomEl =
        mainImageEl.closest('[data-zoom]') ||
        mainImageEl.closest('[data-bgset]') ||
        mainImageEl.closest('[data-image]');
      if (zoomEl) {
        zoomEl.dataset.zoom && (zoomEl.dataset.zoom = resizeImage(newSrc, 2048));
        zoomEl.dataset.bgset && (zoomEl.dataset.bgset = newSrc);
      }

      mainImageEl.src = newSrc;
      ensureResetControl();
      setResetControlVisible(true);

      let imageShown = false;
      const show = () => {
        if (imageShown) return;
        imageShown = true;
        mainImageEl.style.opacity = '1';
        scrollToChangedImage();
      };
      mainImageEl.addEventListener('load', show, { once: true });
      // If cached / already loaded
      if (mainImageEl.complete) show();
    }, CONFIG.transitionMs);
  }

  function restoreOriginal() {
    if (!mainImageEl || !originalSrc) return;
    mainImageEl.style.transition = `opacity ${CONFIG.transitionMs}ms ease`;
    mainImageEl.style.opacity = '0';
    clearTimeout(imageTimer);
    imageTimer = setTimeout(() => {
      mainImageEl.src = originalSrc;
      if (originalSrcset) mainImageEl.setAttribute('srcset', originalSrcset);
      else mainImageEl.removeAttribute('srcset');
      if (originalSizes) mainImageEl.setAttribute('sizes', originalSizes);
      else mainImageEl.removeAttribute('sizes');

      originalPictureSources.forEach(({ element, srcset, sizes }) => {
        if (!element || !document.body.contains(element)) return;
        if (srcset) element.setAttribute('srcset', srcset);
        else element.removeAttribute('srcset');
        if (sizes) element.setAttribute('sizes', sizes);
        else element.removeAttribute('sizes');
      });

      if (originalZoomAttributes?.element) {
        const { element, zoom, bgset, image } = originalZoomAttributes;
        if (zoom) element.setAttribute('data-zoom', zoom);
        else element.removeAttribute('data-zoom');
        if (bgset) element.setAttribute('data-bgset', bgset);
        else element.removeAttribute('data-bgset');
        if (image) element.setAttribute('data-image', image);
        else element.removeAttribute('data-image');
      }

      setResetControlVisible(false);
      mainImageEl.style.opacity = '1';
    }, CONFIG.transitionMs);
  }

  // ========================================================================
  // CORE: Build the image lookup map from bundled_variants metafield
  // ========================================================================

  /**
   * Parse the bundled_variants metafield and fetch images for each
   * child product/variant. Populates the global `imageMap`.
   *
   * bundled_variants structure (from SB metafields):
   * [
   *   {
   *     "variant_title": "Default Title",
   *     "variant_id": 123456,
   *     "handle": "sample_product_handle",
   *     "product_title": "Sample Product",
   *     "variant_options": [{ "name": "Option 1", "value": "Sample Product" }]
   *   },
   *   ...
   * ]
   *
   * The dropdown display text matches variant_options[].value
   * (e.g. "Sample Product", "Custom Water Bottle - Cornflower / Dark Berry")
   */
  function readBundleData() {
    const node = document.querySelector('script[data-sb-image-data]');
    if (node) {
      try { return JSON.parse(node.textContent); } catch (error) {
        console.warn('[SB-IMG] Invalid bundle image data', error);
      }
    }
    return null;
  }

  function selectedParentId(select) {
    if (activeParentVariantId) return String(activeParentVariantId);
    const form = select?.form || document.querySelector('form[id^="product-form-main-"]');
    const id = form?.querySelector('[name="id"]')?.value;
    return String(id || readBundleData()?.selectedVariantId || '');
  }

  async function buildImageMap() {
    imageMap.clear();
    const data = readBundleData();
    const legacy = window.__SB_BUNDLED_VARIANTS__;
    const byVariant = data?.variants || { '': typeof legacy === 'string' ? JSON.parse(legacy) : legacy || [] };
    const handles = [...new Set(Object.values(byVariant).flatMap(rows => rows || []).map(row => row.handle).filter(Boolean))];
    const products = new Map(await Promise.all(handles.map(async handle => [handle, await fetchProduct(handle)])));
    for (const [parentId, rows] of Object.entries(byVariant)) {
      const groups = new Map();
      for (const row of rows || []) {
        const product = products.get(row.handle);
        const variant = product?.variants?.find(item => String(item.id) === String(row.variant_id));
        // A missing variant image must never resolve to another yarn or colour.
        const src = variant?.featured_image?.src;
        if (!src) continue;
        for (const option of row.variant_options || []) {
          if (!option.name || !option.value || /opskrift|pattern|recipe/i.test(option.name)) continue;
          const groupName = normalizeOptionText(option.name);
          if (!groups.has(groupName)) groups.set(groupName, new Map());
          const values = groups.get(groupName);
          const key = normalizeOptionText(option.value);
          const image = resizeImage(src, 1024);
          // Ambiguous values must not silently choose whichever product loaded last.
          if (values.has(key) && values.get(key) !== image) values.set(key, null);
          else values.set(key, image);
        }
      }
      imageMap.set(String(parentId), groups);
    }
  }

  function resolveImage(select) {
    const selected = select.options[select.selectedIndex];
    if (!selected?.value) return null;
    const groups = imageMap.get(selectedParentId(select)) || imageMap.get('');
    if (!groups) return null;
    const property = (select.name || '').match(/^properties\[(.*)\]$/)?.[1];
    const label = select.labels?.[0]?.textContent?.trim();
    const namedGroup = groups.get(normalizeOptionText(property || label || select.name || ''));
    const keys = [selected.value, selected.textContent].map(normalizeOptionText);
    const candidates = namedGroup ? [namedGroup] : [...groups.values()];
    const matches = new Set();
    for (const group of candidates) for (const key of keys) {
      if (group.has(key)) {
        if (!group.get(key)) return null;
        matches.add(group.get(key));
      }
    }
    return matches.size === 1 ? [...matches][0] : null;
  }

  async function handleChange(select) {
    const revision = ++selectionRevision;
    await imageMapReady;
    if (revision !== selectionRevision || !document.contains(select)) return;
    collectYarnSelections();
    const src = resolveImage(select);
    if (src) swapImage(src);
    else restoreOriginal();
  }

  function parentVariantChanged(event) {
    const id = event.detail?.variant?.id;
    if (!id || !imageMap.has(String(id))) return;
    activeParentVariantId = String(id);
    lastYarnImage = null;
    selectedYarnImages = [];
    yarnChoiceCount = 0;
    updateYarnThumbnail();
    if (yarnThumb) yarnThumb.hidden = true;
    ++selectionRevision;
    restoreOriginal();
  }

  function attachListeners(container) {
    const selects = container.querySelectorAll('select');
    selects.forEach((sel) => {
      if (sel.dataset.sbImgBound || /opskrift|pattern|recipe/i.test(sel.name || sel.id || '')) return;
      sel.dataset.sbImgBound = '1';
      sel.addEventListener('change', () => handleChange(sel));
      console.log('[SB-IMG] Listener attached:', sel.name || sel.id || '(unnamed)');
    });
  }

  // ========================================================================
  // INIT
  // ========================================================================

  // Ordinary bundles use Shopify's own gallery navigation, preserving zoom,
  // model photos and the selected variant. Only the thumbnail choices change.
  function initSimpleBundleGallery(data) {
    const variants = data?.simpleVariants;
    if (!variants || !Object.keys(variants).length) return false;
    const gallery = document.querySelector('product-gallery');
    const imageList = gallery?.querySelector('.product-gallery__image-list');
    const nativeThumbs = [...(gallery?.querySelectorAll('.product-gallery__thumbnail') || [])];
    if (!imageList || !nativeThumbs.length) return false;
    const yarnMediaIds = new Set(Object.values(variants).map(v => String(v.mediaId)).filter(id => id !== 'null'));
    const imageKey = src => String(src || '').split(/[?#]/)[0].split('/').pop().replace(/_S(?=\.)/i, '').replace(/_\d+x\d*(?=\.)/, '').toLowerCase();
    const yarnImageKeys = new Set(Object.values(variants).map(v => imageKey(v.image)).filter(Boolean));
    for (const thumb of nativeThumbs) {
      if (yarnImageKeys.has(imageKey(thumb.querySelector('img')?.src))) yarnMediaIds.add(thumb.dataset.mediaId);
    }
    const modelThumbs = nativeThumbs.slice(0, 1).filter(thumb => !yarnMediaIds.has(thumb.dataset.mediaId));
    // A model photo is required; otherwise leave the normal gallery intact.
    if (!modelThumbs.length) return true;
    const control = document.createElement('div');
    control.setAttribute('role', 'group');
    control.setAttribute('aria-label', 'Vælg produktbillede');
    control.style.cssText = 'display:flex;flex-wrap:wrap;gap:8px;margin-top:12px';
    const buttons = [];
    const activate = mediaId => {
      const target = nativeThumbs.find(t => t.dataset.mediaId === String(mediaId));
      if (target) target.click();
    };
    const makeButton = (label, src, mediaId) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.mediaId = mediaId || '';
      button.setAttribute('aria-label', label);
      button.style.cssText = 'width:84px;padding:4px;border:1px solid #ddd;background:transparent;color:inherit;cursor:pointer;font:inherit;font-size:11px';
      const img = document.createElement('img');
      img.src = src || '';
      img.alt = label;
      img.width = 72; img.height = 80;
      img.style.cssText = 'width:72px;height:80px;object-fit:contain;margin:auto';
      const caption = document.createElement('span');
      caption.textContent = label;
      button.append(img, caption);
      button.addEventListener('click', () => activate(button.dataset.mediaId));
      control.append(button); buttons.push(button);
      return button;
    };
    modelThumbs.forEach((thumb, index) => makeButton(index ? `Modelbillede ${index + 1}` : 'Modelbillede', thumb.querySelector('img')?.src, thumb.dataset.mediaId));
    const yarnButton = makeButton('Valgt garn', '', '');
    const updatePressed = () => {
      const active = nativeThumbs.find(t => t.getAttribute('aria-current') === 'true')?.dataset.mediaId;
      for (const button of buttons) {
        const pressed = button.dataset.mediaId === active;
        button.setAttribute('aria-pressed', String(pressed));
        button.style.borderColor = pressed ? '#222' : '#ddd';
      }
    };
    const updateVariant = id => {
      const variant = variants[String(id)];
      const target = variant && nativeThumbs.find(t => t.dataset.mediaId === String(variant.mediaId));
      yarnButton.hidden = !target || !variant.image;
      if (target && variant.image) {
        yarnButton.dataset.mediaId = String(variant.mediaId);
        yarnButton.querySelector('img').src = variant.image;
      }
      updatePressed();
    };
    imageList.insertAdjacentElement('afterend', control);
    // Use the existing theme handlers for images and lightbox behaviour.
    new MutationObserver(updatePressed).observe(gallery, {subtree:true, attributes:true, attributeFilter:['aria-current']});
    document.addEventListener('variant:change', event => {
      const id = event.detail?.variant?.id;
      if (id && variants[String(id)]) updateVariant(id);
    });
    updateVariant(data.selectedVariantId);
    return true;
  }

  async function init() {
    if (initSimpleBundleGallery(readBundleData())) return;
    // 1. Find main image
    mainImageEl = findMainImage();
    if (!mainImageEl) {
      console.warn('[SB-IMG] Main product image not found. Retrying in 1s...');
      clearTimeout(imageTimer);
    imageTimer = setTimeout(() => {
        mainImageEl = findMainImage();
        if (mainImageEl) {
          console.log('[SB-IMG] Main image found on retry.');
          captureOriginalImageState();
          ensureResetControl();
        } else {
          console.error('[SB-IMG] Could not find main product image.');
        }
      }, 1000);
    } else {
      captureOriginalImageState();
      ensureResetControl();
      console.log('[SB-IMG] Main image found:', originalSrc);
    }

    // Build mappings for every parent variant, including alternate yarn qualities.
    imageMapReady = buildImageMap();
    await imageMapReady;
    if (resetControlEl && [...imageMap.values()].some(groups => groups.size)) {
      resetControlEl.hidden = false;
      setResetControlVisible(false);
    }
    document.addEventListener('variant:change', parentVariantChanged);

    // 3. Attach to existing dropdowns
    const ioContainer = document.querySelector(CONFIG.ioBundleContainer);
    if (ioContainer) attachListeners(ioContainer);

    // 4. Watch for dynamically injected dropdowns
    const observer = new MutationObserver((mutations) => {
      for (const mut of mutations) {
        for (const node of mut.addedNodes) {
          if (node.nodeType !== 1) continue;
          const container =
            node.matches && node.matches(CONFIG.ioBundleContainer)
              ? node
              : node.querySelector
                ? node.querySelector(CONFIG.ioBundleContainer)
                : null;
          if (container) attachListeners(container);
          if (node.tagName === 'SELECT') {
            const parent = node.closest(CONFIG.ioBundleContainer);
            if (parent) attachListeners(parent);
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    console.log('[SB-IMG] Initialized.');
  }

  // Start
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

